import Button from '@ui/buttons/Button'
import { DiscountType, isGiftCard } from '@ca/common'
import DiscountItem from '@order/DiscountItem'
import FormInput from '@ui/forms/Input/FormInput'
import { zodResolver } from '@hookform/resolvers/zod'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import { useSavedOrderState } from '@order/hooks/useSavedOrderState'
import { useDiscounts } from '@order/useDiscounts'
import {
  useConfigConditionValue,
  useGetConfigConditionValue,
} from 'features/config/useConfigConditionValue'
import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Platform, View } from 'react-native'
import { z } from 'zod'
import { useGetDiscountOrders } from '../useGetDiscountOrders'
import OrderSelect from './OrderSelect'
import { useConfigConditionGroupCheck } from 'features/config/useConfigCondition'
import validateDiscount from '@screens/order/discounts/validateDiscount'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import InputsSection from '@ui/forms/InputsSection/InputsSection'
import FormRadioGroup from '@ui/forms/CheckboxGroup/FormRadioGroup'
import {
  CouponDiscountOption,
  GiftCardDiscountOption,
  PromotionDiscountOption,
} from '@order/orderTypes'
import { useUniqPromoCodes as useUniqPromoCodes } from './useUniqPromoCodes'
import { DiscountRules } from '@ca/common/types'

type Mode = Extract<DiscountType, 'coupon' | 'gift-card' | 'promotion'>

interface Props {
  open: boolean
  close: () => void
  savedOrderId?: string
  mode: Mode
  codes?: DiscountRules.PromotionItem[]
}

const CouponSchema = z.object({
  code: z
    .string()
    .min(1, 'required')
    .refine((code) => !isGiftCard(code), {
      message: 'voucher.gift-card',
    }),
})

const getTitle = (
  item: CouponDiscountOption | GiftCardDiscountOption | PromotionDiscountOption,
  codes?: DiscountRules.PromotionItem[]
) => {
  if (item.mode === 'promotion') {
    return codes?.find((code) => code.code === item.input.code)?.description
  }
  return item.input.code
}

const GiftCardSchema = z.object({
  code: z
    .string()
    .min(1, 'required')
    .refine((code) => isGiftCard(code), {
      message: 'voucher.gift-card-required',
    }),
})

const PromotionSchema = z.object({
  code: z.string().min(1, 'required'),
})

type DiscountCodeValues = {
  code: string
}

const modeToSchema: Record<
  Mode,
  z.ZodType<DiscountCodeValues, DiscountCodeValues>
> = {
  coupon: CouponSchema,
  'gift-card': GiftCardSchema,
  promotion: PromotionSchema,
}

export default function CouponsModal({
  open,
  close,
  mode,
  savedOrderId,
  codes,
}: Props) {
  const { t } = useTranslation(['ui', 'order', 'user'])
  const text = `order:discounts.${mode}.modal`
  const textForm = `order:discounts.${mode}.modal.sections.form`

  const schema = modeToSchema[mode]

  const form = useForm({
    defaultValues: {
      code: '',
    },
    resolver: zodResolver(schema),
  })

  const { setValue, clearErrors } = form

  const [valid, setValid] = useState<boolean | null>(null)
  const [removeDiscountId, setRemoveDiscountId] = useState<string | null>(null)

  const discounts = useDiscounts(savedOrderId, {
    onSuccess: () => {
      form.reset()
    },
    onError: () => {
      form.setError('code', {
        message: t(`validation:error`),
      })
    },
  })

  const { result, order } = useSavedOrderState(savedOrderId)

  const checkGroup = useConfigConditionGroupCheck()

  const codeConditions = useMemo(() => {
    return checkGroup(`discounts:validation.${mode}`)
  }, [checkGroup, mode])

  const selectCondition = useConfigConditionValue(
    'discounts:orderSelect',
    'discounts'
  )

  const selectOrders =
    order.items.length > 1 && !!selectCondition?.modes.includes(mode)

  const getOrders = useGetDiscountOrders(order.items)
  const codesToShow = useUniqPromoCodes(codes)

  const [selectedOrders, setSelectOrders] = useState<string[]>(() =>
    !result
      ? []
      : getOrders({
        result,
        mode,
      }).map((order) => order.id.toString())
  )

  const isPending = discounts.isPending
  const isEnabled = discounts[mode].enabled

  const getConfigConditionValue = useGetConfigConditionValue(
    'discountsValidation'
  )

  const isDisabled = isPending || !isEnabled

  const isSubmitDisabled = useMemo(() => {
    if (selectOrders && selectedOrders.length === 0) return true
    return false
  }, [selectOrders, selectedOrders])

  const onSubmit = form.handleSubmit(async (values) => {
    if (!isEnabled) return
    if (!result?.orders) return
    const code = values.code.trim()

    if (codeConditions?.valid) {
      for (const condition of codeConditions.valid) {
        const conditionConfig = getConfigConditionValue(condition.name)
        if (!conditionConfig) continue

        const isValid = validateDiscount(code, conditionConfig)
        if (!isValid) {
          form.setError('code', {
            message: t(`validation:invalidCode`),
          })
          return
        }
      }
    }

    const orders = getOrders({
      selectOrders,
      selectedOrders,
      result,
      mode,
    })

    await discounts.add({
      mode,
      input: {
        orders,
        code,
      },
    })
  })

  const onClose = () => {
    form.reset()
    setValid(null)
    close()
  }

  const list = discounts[mode].items
  const isUsed = list.length > 0

  const removeText = 'modals:discounts.remove'

  const handleConfirmRemove = async () => {
    if (!removeDiscountId) return
    await discounts.remove(removeDiscountId)
    setRemoveDiscountId(null)
  }

  const currentCode = useWatch({ control: form.control, name: 'code' })

  const selectedPromoCode = useMemo(() => {
    return codesToShow?.find((code) => code.code === currentCode)
  }, [codesToShow, currentCode])

  const validOrders = selectedPromoCode?.items

  return (
    <>
      <ModalWide
        open={open}
        close={onClose}
        title={t(`${text}.header.title`)}
        desc={t(`${text}.header.desc`)}
        hideBottomSpace
        center
        cardComponentProps={{
          justifyContent: Platform.OS === 'web' ? 'center' : 'flex-end',
        }}
        body={
          <View className="flex w-full flex-col gap-8 px-8 pb-8">
            {isUsed && (
              <InputsSection
                title={t(`${text}.sections.active.title`)}
                desc={t(`${text}.sections.active.desc`)}
              >
                <View className="flex flex-col overflow-hidden rounded-xl border border-black/10">
                  {list.map((item, index) => (
                    <DiscountItem
                      key={item.id}
                      index={index}
                      title={getTitle(item, codes) || undefined}
                      discount={item.discount}
                      onPress={() => setRemoveDiscountId(item.id)}
                    />
                  ))}
                </View>
              </InputsSection>
            )}
            {!isUsed && (
              <InputsSection
                inactive
                title={t(`${text}.sections.empty.title`)}
                desc={t(`${text}.sections.empty.desc`)}
              />
            )}
            <InputsSection
              title={t(`${textForm}.title`)}
              desc={t(`${textForm}.desc`)}
            >
              <View className="flex flex-col items-stretch gap-8">
                <View className="flex w-full flex-1 flex-row items-center">
                  {mode !== 'promotion' && (
                    <FormInput
                      label={t(`${textForm}.inputs.code.label`)}
                      placeholder={t(`${textForm}.inputs.code.placeholder`)}
                      name="code"
                      control={form.control}
                      disabled={isDisabled}
                      className="flex-1"
                      successMessage={
                        valid === true ? t(`validation:success`) : undefined
                      }
                    />
                  )}

                  {mode === 'promotion' && (
                    <FormRadioGroup name="code" control={form.control}>
                      {codesToShow?.map(({ description, code, items }) => (
                        <FormRadioGroup.Item
                          key={code}
                          id={code}
                          disabled={isDisabled}
                          title={description ?? code}
                          onPress={() => {
                            clearErrors('code')
                            setValue('code', code)
                            if (items) {
                              setSelectOrders((current) =>
                                current.filter((order) =>
                                  items.includes(Number(order))
                                )
                              )
                            }
                          }}
                        />
                      ))}
                    </FormRadioGroup>
                  )}
                </View>
                {selectOrders && result?.orders && (
                  <OrderSelect
                    discounts={list}
                    items={result?.orders}
                    order={order}
                    validOrders={validOrders}
                    active={selectedOrders}
                    setActive={setSelectOrders}
                    disabled={isDisabled}
                  />
                )}
                <View className="flex flex-row">
                  <Button
                    variant="outlined"
                    disabled={isDisabled || isSubmitDisabled}
                    label={t(`${textForm}.cta`)}
                    className="w-max self-start"
                    onPress={onSubmit}
                    loading={isPending}
                  />
                </View>
              </View>
            </InputsSection>
          </View>
        }
      />
      <ConfirmationModal
        open={!!removeDiscountId}
        onClose={() => {
          setRemoveDiscountId(null)
        }}
        onCancel={() => {
          setRemoveDiscountId(null)
        }}
        onConfirm={handleConfirmRemove}
        isPending={isPending}
        title={t(`${removeText}.title`)}
        desc={t(`${removeText}.desc`)}
      />
    </>
  )
}
